import { queryOptions } from '@tanstack/react-query';

import { getAccessToken } from '@/lib/tokenStorage';

import type {
  EquipmentResponse,
  StrippingFertilizationResponse,
  UpdateFertilizationRequest,
} from './types';

function getBaseUrl(): string {
  const API_BASE_URL = import.meta.env.VITE_API_BASE_URL;
  if (
    !API_BASE_URL ||
    API_BASE_URL === 'undefined' ||
    API_BASE_URL === 'null'
  ) {
    throw new Error('VITE_API_BASE_URL is not defined or invalid');
  }
  return API_BASE_URL;
}

function getHeaders(): HeadersInit {
  const token = getAccessToken();
  if (!token) {
    throw new Error('No access token available. Please log in.');
  }
  return {
    'Content-Type': 'application/json',
    Authorization: `Bearer ${token}`,
  };
}

async function fetchStrippingFertilizations({
  fertilizationDate,
}: {
  fertilizationDate: string;
}): Promise<StrippingFertilizationResponse[]> {
  const searchParams = new URLSearchParams();
  searchParams.set('fertilizationDate', fertilizationDate);

  const url = `${getBaseUrl()}/api/v1/stripping/fertilizations?${searchParams.toString()}`;

  const response = await fetch(url, {
    method: 'GET',
    credentials: 'include',
    headers: getHeaders(),
  });

  if (!response.ok) {
    throw new Error(`HTTP error! status: ${response.status}`);
  }

  return (await response.json()) as StrippingFertilizationResponse[];
}

async function fetchStrippingEquipments({
  siteId,
}: {
  siteId?: string;
} = {}): Promise<EquipmentResponse[]> {
  const searchParams = new URLSearchParams();
  if (siteId) {
    searchParams.set('siteId', siteId);
  }

  const query = searchParams.toString();
  const url = `${getBaseUrl()}/api/v1/stripping/equipments${query ? `?${query}` : ''}`;

  const response = await fetch(url, {
    method: 'GET',
    credentials: 'include',
    headers: getHeaders(),
  });

  if (!response.ok) {
    throw new Error(`HTTP error! status: ${response.status}`);
  }

  return (await response.json()) as EquipmentResponse[];
}

export const strippingFertilizationsOptions = ({
  fertilizationDate,
}: {
  fertilizationDate: string;
}) =>
  queryOptions({
    queryKey: ['stripping', 'fertilizations', 'list', { fertilizationDate }],
    async queryFn() {
      return fetchStrippingFertilizations({ fertilizationDate });
    },
  });

export const strippingEquipmentsOptions = ({
  siteId,
}: {
  siteId?: string;
} = {}) =>
  queryOptions({
    queryKey: ['stripping', 'equipments', 'list', { siteId }],
    async queryFn() {
      return fetchStrippingEquipments({ siteId });
    },
  });

export async function updateFertilization(
  id: string,
  input: UpdateFertilizationRequest,
): Promise<void> {
  const url = `${getBaseUrl()}/api/v1/stripping/fertilizations/${id}`;

  const response = await fetch(url, {
    method: 'PUT',
    credentials: 'include',
    headers: getHeaders(),
    body: JSON.stringify(input),
  });

  if (!response.ok) {
    const errorText = await response.text();
    throw new Error(
      `HTTP error! status: ${response.status}, message: ${errorText}`,
    );
  }
}
